import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Lock, Trash2 } from "lucide-react";
import { api, apiError, downloadExport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PRINCIPLES = [
  "Original captures are preserved forever, exactly as spoken — AI summaries never overwrite them.",
  "Only you can read your memories, through authenticated MIA services.",
  "Memory content never appears in analytics, logs or admin dashboards.",
  "Export everything or delete everything, whenever you want.",
];

export default function Privacy() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [memConfirm, setMemConfirm] = useState("");
  const [memBusy, setMemBusy] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [delPassword, setDelPassword] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExport();
      toast.success("Your MIA data export has been downloaded.");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const deleteMemories = async () => {
    setMemBusy(true);
    try {
      const { data } = await api.delete("/v1/privacy/memories", { data: { confirmation: memConfirm } });
      toast.success(`Deleted ${data.count} ${data.count === 1 ? "memory" : "memories"}.`);
      setMemConfirm("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMemBusy(false);
    }
  };

  const deleteAccount = async () => {
    setDelBusy(true);
    try {
      await api.delete("/v1/account", { data: { confirmation: delConfirm, password: delPassword } });
      await logout();
      toast.success("Your MIA account and all data have been permanently deleted.");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
      setDelBusy(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="privacy-page">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Privacy & Data</h1>
          <p className="mt-2 text-sm text-muted-foreground">Your memories belong to you. Full control, no dark patterns.</p>
        </div>

        <section className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="privacy-principles">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-muted-foreground" />
            <h2 className="font-display text-lg font-medium">How MIA treats your data</h2>
          </div>
          <ul className="mt-4 space-y-2">
            {PRINCIPLES.map((p) => (
              <li key={p} className="text-sm leading-relaxed text-muted-foreground">— {p}</li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="export-section">
          <h2 className="font-display text-lg font-medium">Export my MIA data</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Download a complete JSON copy of your profile, devices, integrations and every captured memory.
          </p>
          <Button variant="outline" className="mt-4" data-testid="export-data-button" onClick={handleExport} disabled={exporting}>
            <Download className="mr-2 h-4 w-4" /> {exporting ? "Preparing export…" : "Export my data"}
          </Button>
        </section>

        <section className="rounded-2xl border border-destructive/40 bg-card p-6 sm:p-8" data-testid="delete-memories-section">
          <h2 className="font-display text-lg font-medium">Delete all my memories</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Permanently erases every capture from your MIA memory. This cannot be undone. Your account and devices stay active.
          </p>
          <div className="mt-4 max-w-sm space-y-3">
            <Label htmlFor="mem-confirm" className="text-xs">Type DELETE_ALL_MEMORIES to confirm</Label>
            <Input id="mem-confirm" data-testid="delete-memories-confirm-input" value={memConfirm}
                   onChange={(e) => setMemConfirm(e.target.value)} placeholder="DELETE_ALL_MEMORIES" autoComplete="off" />
            <Button variant="destructive" data-testid="delete-memories-confirm-button"
                    onClick={deleteMemories} disabled={memConfirm !== "DELETE_ALL_MEMORIES" || memBusy}>
              <Trash2 className="mr-2 h-4 w-4" /> {memBusy ? "Deleting…" : "Delete all memories"}
            </Button>
          </div>
        </section>

        <section className="rounded-2xl border border-destructive/40 bg-card p-6 sm:p-8" data-testid="delete-account-section">
          <h2 className="font-display text-lg font-medium text-destructive">Delete my account</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Permanently deletes your MIA account, all memories, all devices and all integrations.
            This is immediate and irreversible.
          </p>
          <div className="mt-4 max-w-sm space-y-3">
            <div className="space-y-2">
              <Label htmlFor="del-password" className="text-xs">Your password</Label>
              <Input id="del-password" type="password" data-testid="delete-account-password-input" value={delPassword}
                     onChange={(e) => setDelPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="del-confirm" className="text-xs">Type DELETE to confirm</Label>
              <Input id="del-confirm" data-testid="delete-account-confirm-input" value={delConfirm}
                     onChange={(e) => setDelConfirm(e.target.value)} placeholder="DELETE" autoComplete="off" />
            </div>
            <Button variant="destructive" data-testid="delete-account-confirm-button"
                    onClick={deleteAccount} disabled={delConfirm !== "DELETE" || !delPassword || delBusy}>
              <Trash2 className="mr-2 h-4 w-4" /> {delBusy ? "Deleting account…" : "Permanently delete my account"}
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
}
